import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../FishFinderBuilder.css';
import { getDealLinkById } from '../data/dealLinks';
import CategoryButton from './CategoryButton';
import { fetchLowestPrice, fetchProductPricesByLookup } from '../utils/api';
import { findProductById, getProductImage } from '../utils/productHelpers';
import enhancedAnalytics from '../utils/enhancedAnalytics';
import { screensData } from '../data/screensData';
import { transducersData } from '../data/transducersData';
import { batteriesData } from '../data/batteriesData';
import { shuttlesData } from '../data/shuttlesData';
import { polesData } from '../data/polesData';
import { FaFacebook, FaTwitter, FaSms } from 'react-icons/fa';

const STORAGE_KEY = 'fishFinderBuilderSelections';

// Helper function to find updated product data by id or name
const findUpdatedProductData = (productType, product) => {
  if (!product) return null;
  
  let dataSource;
  switch (productType) {
    case 'screen':
      dataSource = screensData;
      break;
    case 'livescope':
    case 'openwater':
    case 'icefishing':
      dataSource = transducersData;
      break;
    case 'battery':
      dataSource = batteriesData;
      break;
    case 'shuttle':
      dataSource = shuttlesData;
      break;
    case 'pole':
      dataSource = polesData;
      break;
    default:
      return product;
  }
  
  // First try to find by id, then by name
  const updatedData = dataSource.find(item => item.id === product.id) || 
                     dataSource.find(item => item.name === product.name);
  
  // If found, merge with existing data (preserve any custom fields)
  if (updatedData) {
    return { ...product, ...updatedData };
  }
  
  return product;
};

// Helper function to migrate old localStorage data
const migrateStoredData = (savedSelections) => {
  if (!savedSelections) return null;
  
  console.log('🔄 Migrating saved data to include latest product fields...');
  
  const migrated = {
    ...savedSelections,
    selectedScreen: findUpdatedProductData('screen', savedSelections.selectedScreen),
    selectedLivescope: findUpdatedProductData('livescope', savedSelections.selectedLivescope),
    selectedOpenWater: findUpdatedProductData('openwater', savedSelections.selectedOpenWater),
    selectedIceFisher: findUpdatedProductData('icefishing', savedSelections.selectedIceFisher),
    battery: findUpdatedProductData('battery', savedSelections.battery),
    shuttle: findUpdatedProductData('shuttle', savedSelections.shuttle),
    pole: findUpdatedProductData('pole', savedSelections.pole),
  };
  
  // Save the migrated data back to localStorage
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
  } catch (error) {
    console.error('Error saving migrated data to localStorage:', error);
  }
  
  console.log('✅ Data migration completed');
  return migrated;
};

// Helper function to safely read from localStorage
const getSavedSelections = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : null;
    
    // Migrate data if needed
    return migrateStoredData(parsed);
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

// Helper function to safely save to localStorage
const saveSelections = (selections) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const FishFinderBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Force dark theme for fish finder builder
  const theme = {
    isDarkMode: true,
    colors: {
      background: '#111111',
      surface: 'linear-gradient(to right, #1a1a1a, #262626)',
      surfaceHover: 'linear-gradient(135deg, #1a1a1a, #262626)',
      text: '#ffffff',
      textSecondary: 'rgba(255, 255, 255, 0.7)',
      border: 'rgba(255, 255, 255, 0.1)',
      borderHover: 'rgba(255, 255, 255, 0.2)',
      accent: '#4CAF50',
      accentHover: '#3d8b40',
      accentTransparent: 'rgba(76, 175, 80, 0.1)',
      buttonBackground: '#1e1e1e',
      buttonBackgroundHover: '#2a2a2a',
      buttonText: '#0099ff',
      buttonBorder: '#333333',
      cardBackground: '#1a1a1a',
      cardBorder: 'rgba(255, 255, 255, 0.1)',
      cardShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
    }
  };
  
  // Add state for confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Initialize state with saved values
  const savedSelections = getSavedSelections();
  
  // Debug what we're loading from localStorage
  useEffect(() => {
    if (savedSelections) {
      console.log('💾 Loaded from localStorage:', {
        screen: {
          name: savedSelections.selectedScreen?.name,
          csvLookupId: savedSelections.selectedScreen?.csvLookupId
        },
        livescope: {
          name: savedSelections.selectedLivescope?.name,
          csvLookupId: savedSelections.selectedLivescope?.csvLookupId
        },
        openWater: {
          name: savedSelections.selectedOpenWater?.name,
          csvLookupId: savedSelections.selectedOpenWater?.csvLookupId
        },
        iceFisher: {
          name: savedSelections.selectedIceFisher?.name,
          csvLookupId: savedSelections.selectedIceFisher?.csvLookupId
        },
        battery: {
          name: savedSelections.battery?.name,
          csvLookupId: savedSelections.battery?.csvLookupId
        }
      });
    } else {
      console.log('💾 No saved selections found in localStorage');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only log; savedSelections is initial snapshot from getSavedSelections()
  }, []);
  
  const [selectedScreen, setSelectedScreen] = useState(savedSelections?.selectedScreen || null);
  const [selectedLivescope, setSelectedLivescope] = useState(savedSelections?.selectedLivescope || null);
  const [selectedOpenWater, setSelectedOpenWater] = useState(savedSelections?.selectedOpenWater || null);
  const [selectedIceFisher, setSelectedIceFisher] = useState(savedSelections?.selectedIceFisher || null);
  const [battery, setBattery] = useState(savedSelections?.battery || null);
  const [shuttle, setShuttle] = useState(savedSelections?.shuttle || null);
  const [pole, setPole] = useState(savedSelections?.pole || null);
  
  // State for tracking lowest prices from price comparison
  const [lowestPrices, setLowestPrices] = useState({});
  
  // State for sharing functionality
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);

  // Dynamic pricing state for Hot Deals section
  const activeFetches = useRef(new Set());
  const liveScopeProduct = findProductById("livescope-plus-lvs34");
  const bundleProduct = findProductById("Ultra2 106sv + LVS34 Bundle");

  const [liveScopePricing, setLiveScopePricing] = useState({
    bestPrice: null,
    msrp: liveScopeProduct?.msrp || null,
    savings: null,
    loading: true,
    error: null
  });

  const [bundlePricing, setBundlePricing] = useState({
    bestPrice: null,
    msrp: bundleProduct?.msrp || null,
    savings: null,
    loading: true,
    error: null
  });

  // Save selections whenever they change
  useEffect(() => {
    const selections = {
      selectedScreen,
      selectedLivescope,
      selectedOpenWater,
      selectedIceFisher,
      battery,
      shuttle,
      pole
    };
    saveSelections(selections);
  }, [selectedScreen, selectedLivescope, selectedOpenWater, selectedIceFisher, battery, shuttle, pole]);

  // Load selections from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Helper function to find product by ID from data arrays
    const findProductById = (id, dataArray) => {
      return dataArray.find(item => item.id === id) || null;
    };

    // Load selections from URL parameters if they exist
    const screenId = urlParams.get('screen');
    const livescopeId = urlParams.get('livescope');
    const openwaterId = urlParams.get('openwater');
    const icefisherId = urlParams.get('icefisher');
    const batteryId = urlParams.get('battery');
    const shuttleId = urlParams.get('shuttle');
    const poleId = urlParams.get('pole');

    // Debug logging
    console.log('🔍 URL Parameters found:', {
      screenId,
      livescopeId,
      openwaterId,
      icefisherId,
      batteryId,
      shuttleId,
      poleId
    });

    // Only load from URL if we have URL parameters and no existing localStorage data
    const hasUrlParams = screenId || livescopeId || openwaterId || icefisherId || batteryId || shuttleId || poleId;
    const hasLocalStorage = localStorage.getItem(STORAGE_KEY);

    console.log('🔍 Loading conditions:', {
      hasUrlParams,
      hasLocalStorage: !!hasLocalStorage,
      willLoad: hasUrlParams && !hasLocalStorage
    });

    if (hasUrlParams) {
      // Always load from URL parameters if they exist (override localStorage for shared links)
      console.log('🔄 Loading from URL parameters...');
      
      if (screenId) {
        const screen = findProductById(screenId, screensData);
        console.log('🖥️ Screen lookup:', { screenId, found: !!screen, screen });
        if (screen) setSelectedScreen(screen);
      }
      
      if (livescopeId) {
        const livescope = findProductById(livescopeId, transducersData);
        console.log('🎯 LiveScope lookup:', { livescopeId, found: !!livescope, livescope });
        if (livescope) setSelectedLivescope(livescope);
      }
      
      if (openwaterId) {
        const openwater = findProductById(openwaterId, transducersData);
        console.log('🌊 OpenWater lookup:', { openwaterId, found: !!openwater, openwater });
        if (openwater) setSelectedOpenWater(openwater);
      }
      
      if (icefisherId) {
        const icefisher = findProductById(icefisherId, transducersData);
        console.log('🧊 IceFisher lookup:', { icefisherId, found: !!icefisher, icefisher });
        if (icefisher) setSelectedIceFisher(icefisher);
      }
      
      if (batteryId) {
        const batteryProduct = findProductById(batteryId, batteriesData);
        console.log('🔋 Battery lookup:', { batteryId, found: !!batteryProduct, batteryProduct });
        if (batteryProduct) setBattery(batteryProduct);
      }
      
      if (shuttleId) {
        const shuttleProduct = findProductById(shuttleId, shuttlesData);
        console.log('🚀 Shuttle lookup:', { shuttleId, found: !!shuttleProduct, shuttleProduct });
        if (shuttleProduct) setShuttle(shuttleProduct);
      }
      
      if (poleId) {
        const poleProduct = findProductById(poleId, polesData);
        console.log('🎣 Pole lookup:', { poleId, found: !!poleProduct, poleProduct });
        if (poleProduct) setPole(poleProduct);
      }

      // Clean up URL after loading (optional - removes parameters from address bar)
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    }
  }, []); // Empty dependency array - only run on mount

  // Custom state setters that also update localStorage
  const updateSelectedScreen = (newScreen) => {
    setSelectedScreen(newScreen);
    saveSelections({
      ...getSavedSelections(),
      selectedScreen: newScreen
    });
  };

  const updateSelectedLivescope = (newLivescope) => {
    setSelectedLivescope(newLivescope);
    saveSelections({
      ...getSavedSelections(),
      selectedLivescope: newLivescope
    });
  };

  const updateSelectedOpenWater = (newOpenWater) => {
    setSelectedOpenWater(newOpenWater);
    saveSelections({
      ...getSavedSelections(),
      selectedOpenWater: newOpenWater
    });
  };

  const updateSelectedIceFisher = (newIceFisher) => {
    setSelectedIceFisher(newIceFisher);
    saveSelections({
      ...getSavedSelections(),
      selectedIceFisher: newIceFisher
    });
  };

  const updateBattery = (newBattery) => {
    setBattery(newBattery);
    saveSelections({
      ...getSavedSelections(),
      battery: newBattery
    });
  };

  const updateShuttle = (newShuttle) => {
    setShuttle(newShuttle);
    saveSelections({
      ...getSavedSelections(),
      shuttle: newShuttle
    });
  };

  const updatePole = (newPole) => {
    setPole(newPole);
    saveSelections({
      ...getSavedSelections(),
      pole: newPole
    });
  };

  // Deal click handler for analytics
  const handleDealClick = (dealName, retailer, originalPrice, salePrice, dealUrl) => {
    enhancedAnalytics.trackDealClick(dealName, retailer, originalPrice, salePrice, dealUrl);
  };

  // Sharing functionality
  const generateShareableUrl = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    const params = new URLSearchParams();
    
    // Encode current selections in URL parameters
    if (selectedScreen) params.set('screen', selectedScreen.id);
    if (selectedLivescope) params.set('livescope', selectedLivescope.id);
    if (selectedOpenWater) params.set('openwater', selectedOpenWater.id);
    if (selectedIceFisher) params.set('icefisher', selectedIceFisher.id);
    if (battery) params.set('battery', battery.id);
    if (shuttle) params.set('shuttle', shuttle.id);
    if (pole) params.set('pole', pole.id);
    
    return `${baseUrl}?${params.toString()}`;
  };

  const handleCopyLink = () => {
    const shareableUrl = generateShareableUrl();
    navigator.clipboard.writeText(shareableUrl)
      .then(() => {
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 3000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
      });
  };

  const generateQRCodeUrl = () => {
    const shareableUrl = encodeURIComponent(generateShareableUrl());
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${shareableUrl}`;
  };

  const generateShareableText = () => {
    const components = [];
    if (selectedScreen) components.push(selectedScreen.name);
    if (selectedLivescope) components.push(selectedLivescope.name);
    if (selectedOpenWater) components.push(selectedOpenWater.name);
    if (selectedIceFisher) components.push(selectedIceFisher.name);
    if (battery) components.push(battery.name);
    if (shuttle) components.push(shuttle.name);
    if (pole) components.push(pole.name);
    
    const total = formatPrice(calculateTotal());
    return `Check out my custom fish finder build: ${components.join(', ')} - Total: ${total}`;
  };

  // Update state from location when returning from selection flow
  useEffect(() => {
    if (location.state) {
      console.log('🔄 Processing location state:', location.state);
      
      const {
        selectedScreen: newScreen,
        selectedLivescope: newLivescope,
        selectedOpenWater: newOpenWater,
        selectedIceFisher: newIceFisher,
        battery: newBattery,
        shuttle: newShuttle,
        pole: newPole
      } = location.state;

      if (newScreen !== undefined) {
        console.log('📱 Updating screen from location state:', {
          name: newScreen?.name,
          id: newScreen?.id,
          csvLookupId: newScreen?.csvLookupId,
          hasAllFields: Object.keys(newScreen || {}).length
        });
        updateSelectedScreen(newScreen);
      }
      if (newLivescope !== undefined) {
        console.log('🎯 Updating livescope from location state:', {
          name: newLivescope?.name,
          csvLookupId: newLivescope?.csvLookupId
        });
        updateSelectedLivescope(newLivescope);
      }
      if (newOpenWater !== undefined) {
        console.log('🌊 Updating open water from location state:', {
          name: newOpenWater?.name,
          csvLookupId: newOpenWater?.csvLookupId
        });
        updateSelectedOpenWater(newOpenWater);
      }
      if (newIceFisher !== undefined) {
        console.log('🧊 Updating ice fisher from location state:', {
          name: newIceFisher?.name,
          csvLookupId: newIceFisher?.csvLookupId
        });
        updateSelectedIceFisher(newIceFisher);
      }
      if (newBattery !== undefined) {
        console.log('🔋 Updating battery from location state:', {
          name: newBattery?.name,
          csvLookupId: newBattery?.csvLookupId
        });
        updateBattery(newBattery);
      }
      if (newShuttle !== undefined) updateShuttle(newShuttle);
      if (newPole !== undefined) updatePole(newPole);

      // Scroll to top of page when returning from selection pages
      window.scrollTo(0, 0);

      // Clear location state after processing
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch LiveScope Plus LVS34 pricing for Hot Deals
  useEffect(() => {
    if (!liveScopeProduct || activeFetches.current.has('livescope')) {
      if (!liveScopeProduct) {
        setLiveScopePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Product data not found' 
        }));
      }
      return;
    }

    const fetchLiveScopePricing = async () => {
      activeFetches.current.add('livescope');
      
      try {
        const response = await fetchProductPricesByLookup(null, 'LiveScope Plus LVS34', true);
        if (response && response.success && response.data && response.data.length > 0) {
          const prices = response.data.map(item => {
            const priceStr = item.Price || '';
            return parseFloat(priceStr.replace(/[$,]/g, ''));
          }).filter(price => !isNaN(price));
          
          if (prices.length > 0) {
            const bestPrice = Math.min(...prices);
            const savings = liveScopeProduct.msrp - bestPrice;
            
            setLiveScopePricing({
              bestPrice,
              msrp: liveScopeProduct.msrp,
              savings,
              loading: false,
              error: null
            });
          } else {
            setLiveScopePricing(prev => ({ 
              ...prev, 
              loading: false, 
              error: 'No valid prices found' 
            }));
          }
        } else {
          setLiveScopePricing(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'No pricing data available' 
          }));
        }
      } catch (error) {
        setLiveScopePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Unable to load pricing' 
        }));
      } finally {
        activeFetches.current.delete('livescope');
      }
    };

    const timeoutId = setTimeout(fetchLiveScopePricing, 100);
    return () => clearTimeout(timeoutId);
  }, [liveScopeProduct]);

  // Fetch Bundle pricing for Hot Deals
  useEffect(() => {
    if (!bundleProduct || activeFetches.current.has('bundle')) {
      if (!bundleProduct) {
        setBundlePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Product data not found' 
        }));
      }
      return;
    }

    const fetchBundlePricing = async () => {
      activeFetches.current.add('bundle');
      
      try {
        const response = await fetchProductPricesByLookup(null, 'Ultra2 106sv + LVS34 Bundle', true);
        if (response && response.success && response.data && response.data.length > 0) {
          const prices = response.data.map(item => {
            const priceStr = item.Price || '';
            return parseFloat(priceStr.replace(/[$,]/g, ''));
          }).filter(price => !isNaN(price));
          
          if (prices.length > 0) {
            const bestPrice = Math.min(...prices);
            const savings = bundleProduct.msrp - bestPrice;
            
            setBundlePricing({
              bestPrice,
              msrp: bundleProduct.msrp,
              savings,
              loading: false,
              error: null
            });
          } else {
            setBundlePricing(prev => ({ 
              ...prev, 
              loading: false, 
              error: 'No valid prices found' 
            }));
          }
        } else {
          setBundlePricing(prev => ({ 
            ...prev, 
            loading: false, 
            error: 'No pricing data available' 
          }));
        }
      } catch (error) {
        setBundlePricing(prev => ({ 
          ...prev, 
          loading: false, 
          error: 'Unable to load pricing' 
        }));
      } finally {
        activeFetches.current.delete('bundle');
      }
    };

    const timeoutId = setTimeout(fetchBundlePricing, 150);
    return () => clearTimeout(timeoutId);
  }, [bundleProduct]);

  // Function to check if a component is from Amped Outdoors
  const isAmpedOutdoorsComponent = (component) => {
    if (!component) return false;
    
    // Check if component name contains "Amped Outdoors"
    if (component.name && component.name.toLowerCase().includes('amped outdoors')) {
      return true;
    }
    
    // Check if component has dealInfo containing "Amped Outdoors"
    if (component.dealInfo && component.dealInfo.includes('Amped Outdoors')) {
      return true;
    }
    
    return false;
  };

  // Function to fetch lowest price for a component
  const fetchComponentLowestPrice = async (component) => {
    if (!component?.csvLookupId) {
      console.log(`❌ No CSV lookup ID for component: ${component?.name || 'Unknown'}`);
      return null;
    }
    
    const isAmped = isAmpedOutdoorsComponent(component);
    console.log(`🚀 Starting price fetch for: ${component.name} (CSV: ${component.csvLookupId}) with MSRP $${component.msrp} (Amped Outdoors: ${isAmped})`);
    console.log(`🔑 Component ID for storage: "${component.id}"`);
    
    try {
      // Add a timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Price fetch timeout')), 15000)
      );
      
      // Pass the component's MSRP and Amped Outdoors flag to calculate accurate savings
      const lowestPriceData = await Promise.race([
        fetchLowestPrice(component.csvLookupId, true, component.msrp, isAmped),
        timeoutPromise
      ]);
      
      if (lowestPriceData && lowestPriceData.success) {
        console.log(`✅ Price fetched for ${component.name}: $${lowestPriceData.price} (saves $${lowestPriceData.msrpSavings?.amount || 0} vs ${lowestPriceData.msrpSavings?.reference || 'unknown'})`);
        console.log(`💾 Storing price for ID "${component.id}":`, lowestPriceData);
        
        setLowestPrices(prev => {
          const newState = {
            ...prev,
            [component.id]: lowestPriceData
          };
          console.log(`📊 Updated lowestPrices state:`, newState);
          return newState;
        });
        return lowestPriceData;
      } else {
        console.log(`❌ No price data found for ${component.name}`);
      }
    } catch (error) {
      console.error(`Error fetching lowest price for ${component.name}:`, error);
      // Don't let price fetch errors crash the whole component
      setLowestPrices(prev => ({
        ...prev,
        [component.id]: { success: false, price: component.msrp || 0, error: error.message }
      }));
    }
    return null;
  };

  // Add state to track if we're currently fetching prices to prevent multiple simultaneous requests
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);
  
  // Add state to track initial loading
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Effect to fetch lowest prices when components are selected
  useEffect(() => {
    if (isFetchingPrices) {
      console.log('🔍 Already fetching prices, skipping this trigger');
      return;
    }

    const components = [selectedScreen, selectedLivescope, selectedOpenWater, selectedIceFisher, battery, shuttle, pole];
    
    console.log('🔍 Price fetching useEffect triggered. Current lowestPrices state:', lowestPrices);
    console.log('🔍 Components to check for pricing:', components.map(comp => ({
      name: comp?.name,
      id: comp?.id,
      csvLookupId: comp?.csvLookupId,
      hasPrice: !!lowestPrices[comp?.id]
    })));
    
    const componentsNeedingPrices = components.filter(component => 
      component?.csvLookupId && !lowestPrices[component.id]
    );

    if (componentsNeedingPrices.length > 0) {
      setIsFetchingPrices(true);
      console.log(`🔍 Starting price fetch for ${componentsNeedingPrices.length} components`);
      
      Promise.all(componentsNeedingPrices.map(component => {
        console.log(`🔍 Fetching price for component: ${component.name} (ID: ${component.id}, CSV: ${component.csvLookupId})`);
        return fetchComponentLowestPrice(component);
      })).finally(() => {
        setIsFetchingPrices(false);
        setIsInitialLoad(false);
        console.log('🔍 Finished fetching all prices');
      });
    }

    components.forEach(component => {
      if (component?.csvLookupId && lowestPrices[component.id]) {
        console.log(`⏭️ Skipping ${component.name} - already have price: $${lowestPrices[component.id]?.price}`);
      } else if (component && !component.csvLookupId) {
        console.log(`❌ Component ${component.name} has no csvLookupId, skipping price fetch`);
      }
    });

    // Set initial load to false if no components need prices
    if (componentsNeedingPrices.length === 0) {
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScreen, selectedLivescope, selectedOpenWater, selectedIceFisher, battery, shuttle, pole]);

  // Fetch price for LiveScope Plus LVS34 in hot deals section
  useEffect(() => {
    const livescopePlus = {
      id: "livescope-plus-lvs34",
      name: "Garmin LiveScope Plus LVS34",
      csvLookupId: "LiveScope Plus LVS34",
      msrp: 1699.99
    };

    // Only fetch if we don't already have the price (check by ID, not csvLookupId)
    if (!lowestPrices[livescopePlus.id] && !isFetchingPrices) {
      console.log('🔍 Fetching price for hot deals LiveScope Plus LVS34');
      fetchComponentLowestPrice(livescopePlus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchComponentLowestPrice is stable enough; adding it can over-trigger fetches
  }, [lowestPrices, isFetchingPrices]);

  // Calculate total using lowest prices when available, fallback to MSRP
  const calculateTotal = () => {
    const getPrice = (component) => {
      if (!component) return 0;
      try {
        const lowestPrice = lowestPrices[component.id];
        if (lowestPrice && typeof lowestPrice.price === 'number' && !isNaN(lowestPrice.price)) {
          return lowestPrice.price;
        }
        return component.msrp && typeof component.msrp === 'number' && !isNaN(component.msrp) ? component.msrp : 0;
      } catch (error) {
        console.error('Error calculating price for component:', component?.name, error);
        return component?.msrp || 0;
      }
    };

    try {
      const total = (
        getPrice(selectedScreen) +
        getPrice(selectedLivescope) +
        getPrice(selectedOpenWater) +
        getPrice(selectedIceFisher) +
        getPrice(battery) +
        getPrice(shuttle) +
        getPrice(pole)
      );
      return isNaN(total) ? 0 : total;
    } catch (error) {
      console.error('Error calculating total:', error);
      return 0;
    }
  };

  // Calculate total savings compared to MSRP
  const calculateTotalSavings = () => {
    const msrpTotal = (
      (selectedScreen?.msrp || 0) +
      (selectedLivescope?.msrp || 0) +
      (selectedOpenWater?.msrp || 0) +
      (selectedIceFisher?.msrp || 0) +
      (battery?.msrp || 0) +
      (shuttle?.msrp || 0) +
      (pole?.msrp || 0)
    );
    
    const lowestPriceTotal = calculateTotal();
    const savings = msrpTotal - lowestPriceTotal;
    
    return savings > 0 ? savings : 0;
  };

  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleNavigateToSelection = (componentType, transducerType = null) => {
    // For screens, navigate to the new comparison page
    if (componentType === "screen") {
      navigate('/compare-screens', {
        state: {
          selectedScreen,
          selectedLivescope,
          selectedOpenWater,
          selectedIceFisher,
          battery,
          shuttle,
          pole
        }
      });
      return;
    }

    // For battery component, include the active transducer's information
    if (componentType === "battery") {
      // Determine the active transducer based on which one is selected
      const activeTransducer = selectedLivescope || selectedOpenWater || selectedIceFisher;
      
      navigate(`/select/${componentType}`, {
        state: {
          selectedScreen,
          transducer: activeTransducer,
          battery,
          shuttle,
          pole,
        }
      });
      return;
    }

    // For other components, use the existing selection flow
    navigate(`/select/${componentType}`, {
      state: {
        selectedScreen,
        selectedLivescope,
        selectedOpenWater,
        selectedIceFisher,
        battery,
        shuttle,
        pole,
        transducerType
      }
    });
  };

  // Helper function to get product link
  const getProductLink = (component, type) => {
    if (!component) return null;
    
    // Special case for LiveScope Plus LVS34
    if (component.name === "LiveScope Plus LVS34") {
      return "/simple-products/livescope-plus-lvs34";
    }
    
    // Use the simple product pages for all products if they have an ID
    if (component.id) {
      return `/simple-products/${component.id.toLowerCase().replace(/\s+/g, '-')}`;
    }
    
    // Fall back to the old logic if needed
    return component.productLink || getDealLinkById(type, component.id);
  };

  const categories = [
    {
      title: "Screen",
      selected: selectedScreen,
      componentType: "screen",
      description: "",
      price: selectedScreen?.msrp,
      productLink: getProductLink(selectedScreen, 'screen')
    },
    {
      title: "Live Imaging Transducers",
      selected: selectedLivescope,
      componentType: "transducer",
      transducerType: "liveImaging",
      description: "",
      price: selectedLivescope?.msrp || 0,
      productLink: selectedLivescope?.name === "LiveScope Plus LVS34" ? 
        "/simple-products/livescope-plus-lvs34" : 
        getProductLink(selectedLivescope, 'livescope')
    },
    {
      title: "Open Water Transducers",
      selected: selectedOpenWater,
      componentType: "transducer",
      transducerType: "openWater",
      description: "",
      price: selectedOpenWater?.msrp || 0,
      productLink: getProductLink(selectedOpenWater, 'openWater')
    },
    {
      title: "Ice Fishing Transducers",
      selected: selectedIceFisher,
      componentType: "transducer",
      transducerType: "iceFishing",
      description: "",
      price: selectedIceFisher?.msrp || 0,
      productLink: getProductLink(selectedIceFisher, 'iceFishing')
    },
    {
      title: "Battery",
      selected: battery,
      componentType: "battery",
      description: "",
      price: battery?.msrp,
      productLink: getProductLink(battery, 'battery')
    },
    {
      title: "Shuttle",
      selected: shuttle,
      componentType: "shuttle",
      description: "",
      price: shuttle?.msrp,
      productLink: getProductLink(shuttle, 'shuttle')
    },
    {
      title: "Pole Mount",
      selected: pole,
      componentType: "pole",
      description: "",
      price: pole?.msrp,
      productLink: getProductLink(pole, 'pole')
    }
  ];

  // Update clear all function to show confirmation first
  const handleClearAll = () => {
    setShowConfirmation(true);
  };

  // Function to actually clear selections after confirmation
  const confirmClear = () => {
    setSelectedScreen(null);
    setSelectedLivescope(null);
    setSelectedOpenWater(null);
    setSelectedIceFisher(null);
    setBattery(null);
    setShuttle(null);
    setPole(null);
    localStorage.removeItem(STORAGE_KEY);
    setShowConfirmation(false);
  };

  // Function to cancel clearing
  const cancelClear = () => {
    setShowConfirmation(false);
  };

  const handleRemoveItem = (category) => {
    switch (category) {
      case "Screen":
        updateSelectedScreen(null);
        break;
      case "Open Water Transducers":
        updateSelectedOpenWater(null);
        break;
      case "Live Imaging Transducers":
        updateSelectedLivescope(null);
        break;
      case "Ice Fishing Transducers":
        updateSelectedIceFisher(null);
        break;
      case "Battery":
        updateBattery(null);
        break;
      case "Shuttle":
        updateShuttle(null);
        break;
      case "Pole Mount":
        updatePole(null);
        break;
      default:
        break;
    }
  };

  return (
    <div className="fish-finder-builder-page" style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: '0',
      padding: '0 8px',
      background: theme.colors.background,
      minHeight: '100vh',
      position: 'relative'  // Add this for modal positioning
    }}>
      {/* Loading Overlay */}
      {(isFetchingPrices || isInitialLoad) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 999,
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div style={{
            color: '#fff',
            fontSize: '16px',
            fontWeight: '500',
            textAlign: 'center'
          }}>
            {isInitialLoad ? 'Loading your custom builder...' : 'Fetching best prices...'}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: theme.colors.surface,
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: theme.colors.text,
              fontSize: '18px'
            }}>Clear All Selections?</h3>
            <p style={{
              margin: '0 0 24px 0',
              color: theme.colors.textSecondary,
              fontSize: '14px'
            }}>Are you sure you want to clear this build? This action cannot be undone.</p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={cancelClear}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  color: theme.isDarkMode ? '#fff' : '#333',
                  border: `1px solid ${theme.isDarkMode ? '#555' : '#ccc'}`,
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmClear}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}



      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        marginTop: '12px',
        marginBottom: '4px'
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          color: theme.colors.text,
          margin: 0
        }}>Custom Fish Finder Builder</h1>
      </div>

      <div style={{ 
        display: 'flex',
        gap: '0px',
        marginBottom: '17px',
        flexWrap: 'wrap'
      }}>
        {[
          'Choose components & build your bundle',
          'Save with direct links to deals',
          'Ice & open water fishing setups'
        ].map((feature, index) => (
          <div key={index} style={{ 
            display: 'flex',
            alignItems: 'center',
            color: theme.colors.text,
            fontSize: '12px',
            padding: '4px 16px',
            background: 'transparent',
            borderRadius: '8px',
            opacity: 0.9
          }}>
            <span style={{ 
              color: '#4CAF50',
              marginRight: '8px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>✓</span>
            {feature}
          </div>
        ))}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '17px'
      }}>
        {/* Top Sharing Section - Only show when there are selections */}
        {calculateTotal() > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: theme.colors.text,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📤 Share Build:
            </div>
            
            <div style={{
              display: 'flex',
              gap: '6px',
              alignItems: 'center'
            }}>
              {/* Copy Link Button */}
              <button
                onClick={handleCopyLink}
                aria-label="Copy link to clipboard"
                title="Copy Link"
                style={{
                  backgroundColor: "#2c2c2c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#222";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#2c2c2c";
                }}
              >
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </button>

              {/* QR Code Button */}
              <button
                onClick={() => setShowQRCode(true)}
                aria-label="Generate QR Code"
                title="QR Code"
                style={{
                  backgroundColor: "#2c2c2c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#222";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#2c2c2c";
                }}
              >
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM19 13h2v2h-2zM13 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM15 19h2v2h-2zM17 17h2v2h-2zM17 19h2v2h-2zM19 17h2v2h-2z"/>
                </svg>
              </button>
              
              {/* SMS Share Button */}
              <a
                href={`sms:?body=${encodeURIComponent(generateShareableText() + ' ' + generateShareableUrl())}`}
                aria-label="Share via SMS"
                title="SMS"
                style={{
                  backgroundColor: "#2c2c2c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#222";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#2c2c2c";
                }}
              >
                <FaSms size={12} />
              </a>

              {/* Facebook Share Button */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(generateShareableUrl())}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Facebook"
                title="Facebook"
                style={{
                  backgroundColor: "#3b5998",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#2d4373";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#3b5998";
                }}
              >
                <FaFacebook size={12} />
              </a>
              
              {/* Twitter Share Button */}
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(generateShareableUrl())}&text=${encodeURIComponent(generateShareableText())}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Twitter"
                title="Twitter"
                style={{
                  backgroundColor: "#1da1f2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#0d8ddb";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#1da1f2";
                }}
              >
                <FaTwitter size={12} />
              </a>
            </div>
          </div>
        )}
        
        <button
          onClick={handleClearAll}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            color: theme.isDarkMode ? '#fff' : '#333',
            border: `1px solid ${theme.isDarkMode ? '#555' : '#ccc'}`,
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Clear All Selections
        </button>
      </div>

      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        marginBottom: '22px'
      }}>
        {categories.map((category) => (
          <div key={category.title}>
            <h2 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: theme.colors.text,
              margin: 0,
              marginBottom: '4px'
            }}>{category.title}</h2>
            
            {category.selected ? (
              <div style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '12px',
                overflow: 'hidden',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '80px',
                  height: '60px',
                  background: '#fff',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  {category.selected.image && (
                    <img 
                      src={category.selected.image} 
                      alt={category.selected.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://dummyimage.com/80x60/cccccc/000000&text=${category.title}`;
                      }}
                    />
                  )}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.85em',
                    fontWeight: '500',
                    color: theme.colors.text,
                    marginBottom: '4px'
                  }}>{category.selected.name}</div>
                  <div style={{
                    fontSize: '0.85em',
                    color: theme.colors.textSecondary,
                    marginBottom: '8px'
                  }}>
                    {(() => {
                      console.log(`💰 Price check for ${category.selected.name}:`, {
                        hasLowestPrice: !!lowestPrices[category.selected.id],
                        lowestPrices: lowestPrices,
                        csvLookupId: category.selected.csvLookupId
                      });
                      
                      return lowestPrices[category.selected.id] ? (
                        <div>
                          <div style={{ color: '#00a854', fontWeight: '600' }}>
                            Best Price: {formatPrice(lowestPrices[category.selected.id].price)}
                          </div>
                          {lowestPrices[category.selected.id].msrpSavings && (
                            <div style={{ 
                              marginTop: '4px',
                              fontSize: '12px',
                              color: '#e67e22',
                              fontWeight: '500'
                            }}>
                              ↓ Save {formatPrice(lowestPrices[category.selected.id].msrpSavings.amount)} off {lowestPrices[category.selected.id].msrpSavings.reference || 'MSRP'}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          MSRP: {formatPrice(category.selected.msrp)}
                          {category.selected.csvLookupId && (
                            <div style={{ fontSize: '0.7em', color: '#888', marginTop: '2px' }}>
                              📡 Loading best price...
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  
                  {(category.selected?.priceComparisonUrl || category.productLink) && (
                    <Link 
                      to={category.selected?.priceComparisonUrl || category.productLink}
                      style={{
                        padding: '6px 12px',
                        background: theme.colors.accent,
                        color: '#fff',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '0.75em',
                        fontWeight: '500',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background 0.2s ease',
                        marginBottom: '8px',
                        '&:hover': {
                          background: theme.colors.accentHover
                        }
                      }}
                    >
                      💰 Find Best Price
                    </Link>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button 
                    onClick={() => handleNavigateToSelection(category.componentType, category.transducerType)}
                    style={{
                      padding: '8px',
                      background: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '6px',
                      color: theme.colors.text,
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: theme.colors.surfaceHover,
                        borderColor: theme.colors.borderHover
                      }
                    }}
                  >
                    ⇄
                  </button>
                  
                  <button 
                    onClick={() => handleRemoveItem(category.title)}
                    style={{
                      padding: '8px',
                      background: 'transparent',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '6px',
                      color: '#dc3545',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: '#fff5f5',
                        borderColor: '#ff8787'
                      }
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '8px' }}>
                <CategoryButton 
                  onClick={() => handleNavigateToSelection(category.componentType, category.transducerType)} 
                  theme={theme}
                >
                  {category.title === "Screen" ? 'Select Screen' : 
                   category.title === "Open Water Transducers" ? 'Select Open Water Transducer' : 
                   category.title === "Live Imaging Transducers" ? 'Select Live Imaging Transducer' : 
                   category.title === "Ice Fishing Transducers" ? 'Select Ice Transducer' : 
                   category.title === "Battery" ? 'Select Battery' :
                   category.title === "Shuttle" ? 'Select Shuttle' :
                   category.title === "Pole Mount" ? 'Select Pole' :
                   'Select Components'}
                </CategoryButton>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bundle Summary */}
      {calculateTotal() > 0 && (
        <div style={{
          marginBottom: '22px',
          background: theme.isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '12px',
          padding: '20px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '16px',
            color: theme.isDarkMode ? '#fff' : '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🛒 Your Bundle Summary
          </h3>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '14px'
          }}>
            <div>
              <div style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#00a854',
                marginBottom: '4px'
              }}>
                {formatPrice(calculateTotal())}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.isDarkMode ? '#e0e0e0' : '#666'
              }}>
                Best Available Prices
              </div>
            </div>
            {calculateTotalSavings() > 0 && (
              <div style={{
                textAlign: 'right'
              }}>
                <div style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#e67e22',
                  marginBottom: '4px',
                  textShadow: theme.isDarkMode ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  💰 Save {formatPrice(calculateTotalSavings())}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: theme.isDarkMode ? '#e0e0e0' : '#666'
                }}>
                  vs. MSRP pricing
                </div>
              </div>
            )}
          </div>

          {/* Compact Sharing Section */}
          <div style={{
            borderTop: `1px solid ${theme.colors.border}`,
            paddingTop: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: theme.isDarkMode ? '#fff' : '#333',
                marginBottom: '4px'
              }}>
                📤 Share Your Build
              </div>
              <div style={{
                fontSize: '12px',
                color: theme.isDarkMode ? '#b0b0b0' : '#666'
              }}>
                Save or share this exact configuration
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              {/* Copy Link Button */}
              <button
                onClick={handleCopyLink}
                aria-label="Copy link to clipboard"
                title="Copy Link"
                style={{
                  backgroundColor: "#2c2c2c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#222";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#2c2c2c";
                }}
              >
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                </svg>
              </button>

              {/* QR Code Button */}
              <button
                onClick={() => setShowQRCode(true)}
                aria-label="Generate QR Code"
                title="QR Code"
                style={{
                  backgroundColor: "#2c2c2c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#222";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#2c2c2c";
                }}
              >
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM19 13h2v2h-2zM13 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM15 19h2v2h-2zM17 17h2v2h-2zM17 19h2v2h-2zM19 17h2v2h-2z"/>
                </svg>
              </button>
              
              {/* SMS Share Button */}
              <a
                href={`sms:?body=${encodeURIComponent(generateShareableText() + ' ' + generateShareableUrl())}`}
                aria-label="Share via SMS"
                title="SMS"
                style={{
                  backgroundColor: "#2c2c2c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#222";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#2c2c2c";
                }}
              >
                <FaSms size={14} />
              </a>

              {/* Facebook Share Button */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(generateShareableUrl())}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Facebook"
                title="Facebook"
                style={{
                  backgroundColor: "#3b5998",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#2d4373";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#3b5998";
                }}
              >
                <FaFacebook size={14} />
              </a>
              
              {/* Twitter Share Button */}
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(generateShareableUrl())}&text=${encodeURIComponent(generateShareableText())}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Twitter"
                title="Twitter"
                style={{
                  backgroundColor: "#1da1f2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  cursor: "pointer",
                  transition: "background-color 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#0d8ddb";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#1da1f2";
                }}
              >
                <FaTwitter size={14} />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Share Your Build Section */}


      {/* Hot Deals Section - Dynamic from Landing Page */}
      <div style={{
        marginBottom: '22px',
        background: '#1e1e1e', // Slightly lighter than main background for separation
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: theme.colors.text,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            💰 Today's Best Garmin Deals
          </h2>
        </div>
        
        <div style={{
          padding: '16px 20px',
          overflowX: 'auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
            gap: '18px'
          }}>
            {/* Deal 1: Ultra2 106sv + LVS34 Bundle */}
            <Link 
              to="/simple-products/Ultra2 106sv + LVS34 Bundle" 
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                position: 'relative'
              }}
              onClick={() => {
                if (bundlePricing.bestPrice && bundlePricing.msrp) {
                  handleDealClick(
                    'Garmin Ultra2 106sv + LVS34 Bundle',
                    'Best Price Available',
                    bundlePricing.msrp,
                    bundlePricing.bestPrice,
                    '/simple-products/Ultra2 106sv + LVS34 Bundle'
                  );
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                height: '105px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#ffffff',
                margin: 0
              }}>
                <img 
                  src={getProductImage(bundleProduct)} 
                  alt="Garmin Ultra2 106sv + LVS34 Bundle"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    background: '#ffffff',
                    padding: '6px'
                  }}
                />
              </div>
              <div style={{ padding: '12px' }}>
                <h3 style={{
                  fontSize: '0.95rem',
                  marginBottom: '4px',
                  lineHeight: '1.2',
                  fontWeight: '600',
                  color: theme.colors.text
                }}>Garmin Ultra2 106sv + LVS34 Bundle</h3>
                <div style={{
                  color: '#666666',
                  fontSize: '0.8rem',
                  marginBottom: '6px',
                  fontStyle: 'italic',
                  lineHeight: '1.1'
                }}>10" screen + LiveScope Plus</div>
                <div style={{ marginBottom: '8px' }}>
                  {bundlePricing.loading ? (
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: '0.9rem'
                    }}>Loading pricing...</div>
                  ) : bundlePricing.error ? (
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: '0.9rem'
                    }}>Pricing unavailable</div>
                  ) : bundlePricing.bestPrice ? (
                    <>
                      <div style={{
                        color: theme.colors.text,
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        lineHeight: '1.2',
                        marginBottom: '4px'
                      }}>${bundlePricing.bestPrice.toFixed(2)}</div>
                      {bundlePricing.savings && bundlePricing.savings > 0 && (
                        <div style={{
                          display: 'inline-block',
                          backgroundColor: '#00C851', // Green for dark theme
                          color: 'white',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          padding: '2px 8px',
                          borderRadius: '16px',
                          lineHeight: '1.2'
                        }}>Save ${bundlePricing.savings.toFixed(2)}</div>
                      )}
                    </>
                  ) : (
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: '0.9rem'
                    }}>Price N/A</div>
                  )}
                </div>
              </div>
            </Link>

            {/* Deal 2: LiveScope Plus LVS34 */}
            <Link 
              to="/simple-products/livescope-plus-lvs34" 
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                position: 'relative'
              }}
              onClick={() => {
                if (liveScopePricing.bestPrice && liveScopePricing.msrp) {
                  handleDealClick(
                    'Garmin LiveScope Plus LVS34',
                    'Best Price Available',
                    liveScopePricing.msrp,
                    liveScopePricing.bestPrice,
                    '/simple-products/livescope-plus-lvs34'
                  );
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                height: '105px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#ffffff',
                margin: 0
              }}>
                <img 
                  src="/images/transducers/livescopeplus.jpg" 
                  alt="Garmin LiveScope Plus LVS34"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    background: '#ffffff',
                    padding: '6px'
                  }}
                />
              </div>
              <div style={{ padding: '12px' }}>
                <h3 style={{
                  fontSize: '0.95rem',
                  marginBottom: '4px',
                  lineHeight: '1.2',
                  fontWeight: '600',
                  color: theme.colors.text
                }}>Garmin LiveScope Plus LVS34</h3>
                <div style={{
                  color: '#666666',
                  fontSize: '0.8rem',
                  marginBottom: '6px',
                  fontStyle: 'italic',
                  lineHeight: '1.1'
                }}>Most popular forward-facing sonar</div>
                <div style={{ marginBottom: '8px' }}>
                  {liveScopePricing.loading ? (
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: '0.9rem'
                    }}>Loading pricing...</div>
                  ) : liveScopePricing.error ? (
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: '0.9rem'
                    }}>Pricing unavailable</div>
                  ) : liveScopePricing.bestPrice ? (
                    <>
                      <div style={{
                        color: theme.colors.text,
                        fontWeight: '700',
                        fontSize: '1.1rem',
                        lineHeight: '1.2',
                        marginBottom: '4px'
                      }}>${liveScopePricing.bestPrice.toFixed(2)}</div>
                      {liveScopePricing.savings && liveScopePricing.savings > 0 && (
                        <div style={{
                          display: 'inline-block',
                          backgroundColor: '#00C851', // Green for dark theme
                          color: 'white',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          padding: '2px 8px',
                          borderRadius: '16px',
                          lineHeight: '1.2'
                        }}>Save ${liveScopePricing.savings.toFixed(2)}</div>
                      )}
                    </>
                  ) : (
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: '0.9rem'
                    }}>Price N/A</div>
                  )}
                </div>
              </div>
            </Link>
          </div>
          
          {/* View All Deals Button */}
          <div style={{
            textAlign: 'center',
            marginTop: '20px'
          }}>
            <Link 
              to="/price-comparison" 
              style={{
                display: 'inline-block',
                background: 'transparent',
                color: '#4CAF50',
                border: '2px solid #4CAF50',
                padding: '8px 20px',
                borderRadius: '4px',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#4CAF50';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(76, 175, 80, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#4CAF50';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              🔍 See All Gear Prices
            </Link>
          </div>
        </div>
      </div>



      {/* Copy Link Toast */}
      {showCopyToast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: theme.isDarkMode ? '#2ecc71' : '#27ae60',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '600'
        }}>
          ✓ Build link copied to clipboard!
        </div>
      )}

      {/* QR Code Modal */}
      {showQRCode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: theme.colors.surface,
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '90%',
            maxHeight: '90%',
            position: 'relative',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}>
            <button
              onClick={() => setShowQRCode(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: theme.colors.text,
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
            <h3 style={{
              margin: '0 0 20px 0',
              color: theme.colors.text,
              fontSize: '1.2rem'
            }}>
              QR Code for Your Custom Build
            </h3>
            <img
              src={generateQRCodeUrl()}
              alt="QR Code for current build"
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
                border: '1px solid #ddd',
                borderRadius: '8px'
              }}
            />
            <p style={{
              margin: '15px 0 0 0',
              fontSize: '14px',
              color: theme.isDarkMode ? '#aaa' : '#666'
            }}>
              Scan with your phone to access this build
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FishFinderBuilder; 